export type EventsArchiveFilter = 'active' | 'historical';

export type EventsListFilterable = {
  name: string;
  type?: string | null;
  region?: string | null;
  summary?: string | null;
  created_at: string;
  archived: boolean;
};

export type EventsListFilters = {
  search: string;
  createdFrom: string;
  createdTo: string;
  aorNames: string[];
  archive: EventsArchiveFilter;
};

export const EMPTY_EVENTS_LIST_FILTERS: EventsListFilters = {
  search: '',
  createdFrom: '',
  createdTo: '',
  aorNames: [],
  archive: 'active',
};

function filterBoundMs(localDateTime: string): number | null {
  if (!localDateTime) return null;
  const [datePart, timePart] = localDateTime.split('T');
  if (!datePart || datePart.length < 10) return null;
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return null;
  const [hour, minute] = (timePart ?? '00:00').split(':').map(Number);
  return new Date(year, month - 1, day, hour || 0, minute || 0).getTime();
}

function chipDateTimeLabel(value: string): string {
  return value.replace('T', ' ').slice(0, 16);
}

function regionTokens(region: string | null | undefined): string[] {
  if (!region) return [];
  return region
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
}

export function eventMatchesSearch(
  event: Pick<EventsListFilterable, 'name' | 'type' | 'region' | 'summary'>,
  search: string
): boolean {
  const term = search.trim().toLowerCase();
  if (!term) return true;
  const haystack = [event.name, event.type, event.region, event.summary]
    .filter((value): value is string => Boolean(value))
    .join(' ')
    .toLowerCase();
  return haystack.includes(term);
}

export function eventMatchesCreatedRange(
  event: Pick<EventsListFilterable, 'created_at'>,
  createdFrom: string,
  createdTo: string
): boolean {
  if (!createdFrom && !createdTo) return true;
  const eventMs = new Date(event.created_at).getTime();
  if (Number.isNaN(eventMs)) return false;
  const fromMs = filterBoundMs(createdFrom);
  const toMs = filterBoundMs(createdTo);
  if (fromMs != null && eventMs < fromMs) return false;
  if (toMs != null && eventMs > toMs) return false;
  return true;
}

export function eventMatchesAorNames(
  event: Pick<EventsListFilterable, 'region'>,
  aorNames: string[]
): boolean {
  if (aorNames.length === 0) return true;
  const tokens = regionTokens(event.region);
  if (tokens.length === 0) return false;
  return aorNames.some((name) => tokens.includes(name.trim().toLowerCase()));
}

export function eventMatchesArchive(
  event: Pick<EventsListFilterable, 'archived'>,
  archive: EventsArchiveFilter
): boolean {
  return archive === 'historical' ? event.archived : !event.archived;
}

export function hasEventsListNarrowingFilters(
  filters: Pick<EventsListFilters, 'search' | 'createdFrom' | 'createdTo' | 'aorNames'>
): boolean {
  return (
    filters.search.trim().length > 0 ||
    filters.createdFrom.length > 0 ||
    filters.createdTo.length > 0 ||
    filters.aorNames.length > 0
  );
}

export type EventsListFilterChip = {
  id: string;
  label: string;
};

export function buildEventsListFilterChips(filters: {
  search: string;
  createdFrom: string;
  createdTo: string;
  aors: { id: string; name: string }[];
  archive: EventsArchiveFilter;
}): EventsListFilterChip[] {
  const chips: EventsListFilterChip[] = [];
  const search = filters.search.trim();
  if (search) {
    chips.push({ id: 'search', label: `Search: ${search}` });
  }
  if (filters.createdFrom) {
    chips.push({
      id: 'createdFrom',
      label: `From ${chipDateTimeLabel(filters.createdFrom)}`,
    });
  }
  if (filters.createdTo) {
    chips.push({
      id: 'createdTo',
      label: `To ${chipDateTimeLabel(filters.createdTo)}`,
    });
  }
  for (const aor of filters.aors) {
    chips.push({ id: `aor:${aor.id}`, label: aor.name });
  }
  if (filters.archive === 'historical') {
    chips.push({ id: 'archive', label: 'Archived' });
  }
  return chips;
}

export function filterOrgEvents<T extends EventsListFilterable>(
  events: T[],
  filters: EventsListFilters
): T[] {
  return events.filter(
    (event) =>
      eventMatchesArchive(event, filters.archive) &&
      eventMatchesSearch(event, filters.search) &&
      eventMatchesCreatedRange(event, filters.createdFrom, filters.createdTo) &&
      eventMatchesAorNames(event, filters.aorNames)
  );
}
